// Studby — subscription/trial helpers shown across feature pages.
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Lock, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

// Full-page paywall shown when the user has no active trial/subscription.
export function Paywall() {
  return (
    <div className="max-w-md mx-auto text-center py-10" data-testid="paywall">
      <span className="grid place-items-center w-14 h-14 rounded-2xl bg-secondary text-primary mx-auto mb-5">
        <Lock size={26} aria-hidden="true" />
      </span>
      <h2 className="font-heading text-2xl font-bold mb-2">Start your free trial</h2>
      <p className="text-muted-foreground mb-6">
        Try Studby free for 3 days, then <span className="font-medium text-foreground">€3.99/mo</span> for
        50 questions a month or <span className="font-medium text-foreground">€12.99/mo</span> for 250 questions (Pro).
        Cancel anytime.
      </p>
      <Link to="/app/subscribe">
        <Button size="lg" className="rounded-full" data-testid="paywall-subscribe-button">
          <Sparkles size={18} className="mr-1.5" aria-hidden="true" /> Start free trial
        </Button>
      </Link>
    </div>
  );
}

// Quota banner: trialing (days left + questions/day) or basic-plan monthly usage.
export function TrialBanner() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  if (!user) return null;
  const isTrial = user.subscription_status === "trialing";
  const isBasic = user.subscription_status === "active" && user.plan === "basic";
  if (!isTrial && !isBasic) return null; // pro / none see no banner

  const daysLeft = isTrial && user.trial_ends_at
    ? Math.max(0, Math.ceil((new Date(user.trial_ends_at) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;
  const remaining = stats?.remaining;
  const limit = stats?.limit;
  const periodWord = isTrial ? "today" : "this month";

  return (
    <div
      className="flex items-center justify-between gap-3 bg-secondary border border-border rounded-xl px-4 py-3 mb-6"
      data-testid="trial-banner"
    >
      <p className="text-sm text-primary font-medium">
        <Sparkles size={15} className="inline mr-1.5 -mt-0.5" aria-hidden="true" />
        {isTrial
          ? (daysLeft > 0
              ? `Free trial: ${daysLeft} day${daysLeft === 1 ? "" : "s"} left (billed after unless cancelled)`
              : "Trial ends today — you'll be billed unless you cancel")
          : "Basic plan"}
        {remaining != null && limit != null && (
          <span className="text-muted-foreground font-normal">
            {" · "}{remaining}/{limit} left {periodWord}
          </span>
        )}
      </p>
      <Link to="/app/subscribe">
        <Button size="sm" variant="outline" className="rounded-full" data-testid="banner-upgrade-button">
          {isBasic ? "Go Pro" : "Manage"}
        </Button>
      </Link>
    </div>
  );
}
